// Purpose: Let an assigned volunteer verify one duty, opt into SMS reminders, and create an account.
// Dependencies: event context, Firebase Auth/Functions, shared entry account signup controls.
// Behavior: Duty details come only from the server after assignment authorization succeeds.

import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import EntryAccountSignup from "../../components/entry_account_signup";
import { completeEmailRegistration } from "../../services/email_registration";
import { formatDateTime } from "../../utils/date_format";
import { showStatus } from "../../utils/status_helpers";
import FullPageSpinner from "../../components/FullPageSpinner";
import { sanitizeHtml } from "../../utils/sanitize_html";

export default function VolunteerVerification() {
  const { activityId, slotId } = useParams();
  const { eventId, eventLoading, ready } = useEventContext({ redirectIfNotFound: true });
  const { user } = useAuth();
  const [details, setDetails] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const registrationUserRef = useRef(null);

  const loadDetails = async () => {
    if (!user || !eventId) return;
    setLoading(true);
    try {
      const result = await httpsCallable(functions, "getVolunteerVerification")({ eventId, activityId, slotId });
      setDetails(result.data);
    } catch (error) {
      showStatus(setStatus, { type: "error", message: error?.message || "Unable to load this volunteer duty." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDetails(); }, [user, eventId, activityId, slotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    if (!createAccount) return;
    setLoading(true);
    try {
      if (!registrationUserRef.current) {
        registrationUserRef.current = (await createUserWithEmailAndPassword(auth, form.email.trim(), password)).user;
        setPassword("");
      }
      await completeEmailRegistration(registrationUserRef.current, { ...form, agreedToTerms });
      showStatus(setStatus, { type: "success", message: "Account created. Verify your email to finish account setup; your volunteer duty is ready to review." });
    } catch (error) {
      showStatus(setStatus, { type: "error", message: error?.message || "Unable to create your account." });
    } finally {
      setLoading(false);
    }
  };

  const updateSmsConsent = async (optedIn) => {
    setLoading(true);
    try {
      await httpsCallable(functions, "setVolunteerReminderConsent")({ eventId, activityId, slotId, optedIn });
      setDetails((current) => ({ ...current, smsReminderConsent: optedIn }));
      showStatus(setStatus, { type: "success", message: optedIn ? "SMS reminder preference saved." : "SMS reminders stopped." });
    } catch (error) {
      showStatus(setStatus, { type: "error", message: error?.message || "Unable to update SMS reminders." });
    } finally {
      setLoading(false);
    }
  };

  if (!ready || eventLoading) return <FullPageSpinner message="Loading volunteer verification..." />;

  return <section id="top-content" className="container py-5" style={{ maxWidth: "800px" }}>
    <h1 className="display-6">Volunteer Duty Verification</h1>
    <p className="lead text-muted">Review your volunteer assignment and event-day instructions.</p>
    {status?.message && <div role="alert" className={`alert ${status.type === "success" ? "alert-success" : "alert-danger"}`}>{status.message}</div>}

    {!user && <form className="card shadow-sm" onSubmit={handleCreateAccount}>
      <div className="card-body">
        <h2 className="h4">Create an account or sign in</h2>
        <p className="text-muted">Use the email address that received the volunteer invitation.</p>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label" htmlFor="volunteer-first-name">First name</label><input id="volunteer-first-name" className="form-control" required value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} /></div>
          <div className="col-md-6"><label className="form-label" htmlFor="volunteer-last-name">Last name</label><input id="volunteer-last-name" className="form-control" required value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} /></div>
          <div className="col-12"><label className="form-label" htmlFor="volunteer-email">Email</label><input id="volunteer-email" type="email" className="form-control" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></div>
        </div>
        <EntryAccountSignup user={user} accountCreated={Boolean(registrationUserRef.current)} createAccount={createAccount} onCreateAccountChange={(checked) => { setCreateAccount(checked); setPassword(""); }} password={password} onPasswordChange={setPassword} agreedToTerms={agreedToTerms} onTermsChange={setAgreedToTerms} disabled={loading} />
        <button type="submit" className="btn btn-primary mt-3" disabled={loading || !createAccount}>{loading ? "Creating account..." : "Create account and verify duty"}</button>
      </div>
    </form>}

    {user && loading && !details && <FullPageSpinner message="Loading your volunteer duty..." />}
    {user && details && <div className="card shadow-sm"><div className="card-body">
      <h2 className="h4">{details.activityTitle}</h2>
      <p><strong>Event:</strong> {details.eventName}<br /><strong>Time:</strong> {formatDateTime(details.startTime)} – {formatDateTime(details.endTime)}</p>
      {details.volunteerCheckin && <div className="mb-3"><h3 className="h5">Check-in instructions</h3><p>{details.volunteerCheckin}</p></div>}
      {details.contactNote && <div className="mb-3"><h3 className="h5">Contact the Event Promoter</h3><div dangerouslySetInnerHTML={{ __html: sanitizeHtml(details.contactNote) }} /></div>}
      {details.volunteerGoodies && <div className="mb-3"><h3 className="h5">Volunteer Goodies</h3><div dangerouslySetInnerHTML={{ __html: sanitizeHtml(details.volunteerGoodies) }} /></div>}
      <div className="border rounded p-3"><h3 className="h5">SMS check-in reminder</h3><p className="mb-2">Get a reminder 20 minutes before your shift to check in at the volunteer booth.</p><p className="small text-muted">SMS delivery is pending event messaging setup. You can save your preference now.</p><button type="button" className={`btn ${details.smsReminderConsent ? "btn-outline-secondary" : "btn-primary"}`} onClick={() => updateSmsConsent(!details.smsReminderConsent)} disabled={loading}>{details.smsReminderConsent ? "Stop SMS reminders" : "Save SMS reminder preference"}</button></div>
    </div></div>}
  </section>;
}
